import { makeStyles } from '@material-ui/core/styles'
import MuiDialog from '@material-ui/core/Dialog'
import MuiDialogActions from '@material-ui/core/DialogActions'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import useDialog from './useDialog'
import Button from '../button/button'
import useTranslate from '../../i18n/useTranslate'
import Grid from '@material-ui/core/Grid'
import { useRef } from 'react'

type ActionAlertType = {
    txtConfirm?: string
    txtClose?: string
    isActionReady: boolean
    onClickConfirm?: () => void
    onClickClose?: () => void
}
const ActionAlertMessage = (props: ActionAlertType) => {
    const translate = useTranslate()
    const { txtClose = translate.get('BUTTON.CLOSE'), onClickClose } = props

    return (
        <Button variant="contained" color="primary" onClick={onClickClose}>
            {txtClose}
        </Button>
    )
}
const ActionAlertConfirm = (props: ActionAlertType) => {
    const translate = useTranslate()
    const {
        txtConfirm = translate.get('BUTTON.CONFIRM'),
        txtClose = translate.get('BUTTON.CLOSE'),
        onClickConfirm,
        onClickClose,
    } = props

    return (
        <>
            <Button
                variant="contained"
                color="inherit"
                onClick={onClickClose}
                style={{ backgroundColor: '#FFFFFF', border: 'solid 1px #db5f8e' }}
            >
                {txtClose}
            </Button>
            <Button
                variant="contained"
                color="primary"
                style={{ border: 'solid 1px #db5f8e' }}
                onClick={onClickConfirm}
            >
                {txtConfirm}
            </Button>
        </>
    )
}

const ActionAlertSaveCancel = (props: ActionAlertType) => {
    const translate = useTranslate()
    const {
        txtConfirm = translate.get('BUTTON.SAVE'),
        txtClose = translate.get('BUTTON.CANCEL'),
        isActionReady,
        onClickConfirm,
        onClickClose,
    } = props

    return (
        <>
            <Button
                variant="contained"
                color="inherit"
                onClick={onClickClose}
                style={{ backgroundColor: '#FFFFFF', border: 'solid 1px #db5f8e' }}
            >
                {txtClose}
            </Button>
            <Button
                variant="contained"
                color="primary"
                style={{ border: 'solid 1px #db5f8e' }}
                onClick={onClickConfirm}
                disabled={!isActionReady}
            >
                {txtConfirm}
            </Button>
        </>
    )
}

const ActionAlertAcceptCancel = (props: ActionAlertType) => {
    const translate = useTranslate()
    const {
        txtConfirm = translate.get('BUTTON.ACCEPT'),
        txtClose = translate.get('BUTTON.CANCEL'),
        isActionReady,
        onClickConfirm,
        onClickClose,
    } = props

    return (
        <>
            <Button
                variant="contained"
                color="inherit"
                onClick={onClickClose}
                style={{ backgroundColor: '#FFFFFF', border: 'solid 1px #db5f8e' }}
            >
                {txtClose}
            </Button>
            <Button
                variant="contained"
                color="primary"
                style={{ border: 'solid 1px #db5f8e' }}
                onClick={onClickConfirm}
                disabled={!isActionReady}
            >
                {txtConfirm}
            </Button>
        </>
    )
}

type DialogViewType = ReturnType<typeof useDialog> & DialogType
const DialogView = (props: DialogViewType) => {
    const {
        isOpen,
        type = 'alert-message',
        maxWidth = 'sm',
        title,
        content,
        onClickConfirm,
        onClickClose,
        isActionReady,
    } = props
    const classes = useStyles()

    const renderContent = () => {
        switch (type) {
            case 'alert-confirm':
                return content
            case 'dialog-element':
                return <>{content}</>
            case 'dialog-element-accept':
                return <>{content}</>
            default:
                //alert-message
                return <Grid className={classes.contentText}>{content}</Grid>
        }
    }

    const renderAction = () => {
        switch (type) {
            case 'alert-confirm':
                return (
                    <ActionAlertConfirm
                        isActionReady={true}
                        onClickConfirm={onClickConfirm}
                        onClickClose={onClickClose}
                    />
                )
            case 'alert-message':
                return <ActionAlertMessage isActionReady={true} onClickClose={onClickClose} />
            case 'dialog-element':
                return (
                    <ActionAlertSaveCancel
                        isActionReady={isActionReady}
                        onClickConfirm={onClickConfirm}
                        onClickClose={onClickClose}
                    />
                )
            case 'dialog-element-accept':
                return (
                    <ActionAlertAcceptCancel
                        isActionReady={isActionReady}
                        onClickConfirm={onClickConfirm}
                        onClickClose={onClickClose}
                    />
                )
            default:
                //alert-message
                return <></>
        }
    }

    const listInnerRef = useRef<any>({
        scrollTop: 0,
        scrollHeight: 0,
        clientHeight: 0,
    })

    const onScroll = () => {
        if (listInnerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current
            if (Math.ceil(scrollTop) + clientHeight >= scrollHeight) {
                props.setMainActionReady()
            }
        }
    }

    return (
        <MuiDialog
            open={isOpen}
            maxWidth={maxWidth}
            disableBackdropClick
            onClose={onClickClose}
            classes={{ paper: classes.root }}
        >
            <MuiDialogTitle className={classes.title}>{title}</MuiDialogTitle>
            <MuiDialogContent onScroll={onScroll} ref={listInnerRef} id="pdpa">
                {renderContent()}
            </MuiDialogContent>
            {/* <div>test</div> */}
            <MuiDialogActions>{renderAction()}</MuiDialogActions>
        </MuiDialog>
    )
}

export type DialogType = {
    title?: string
    content?: string | JSX.Element
    type?: 'alert-message' | 'alert-confirm' | 'dialog-element' | 'dialog-element-accept' | ''
    maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false
    onRef: (func: Record<string, Function>) => void
    onClickConfirm?: () => void
    onAfterClose?: () => void
    reachedBottom?: boolean
}
const Dialog = (props: DialogType) => {
    const dialogModel = useDialog(props)
    return <DialogView {...props} {...dialogModel} />
}

const useStyles = makeStyles((theme) => ({
    root: {
        minWidth: '600px',
        [theme.breakpoints.down('xs')]: {
            minWidth: '320px',
        },
    },

    title: {
        color: 'white',
        backgroundColor: theme.palette.primary.main,
        padding: '8px 24px',
    },
    contentText: {
        color: theme.palette.text.primary,
    },
}))

export default Dialog
